import { Grid, Typography, Box } from '@material-ui/core';
import React, { useState, useEffect, useContext } from 'react';
import DialogComp from '../../../../../Core/Modal/dialogModal';
import { getCustomerDetails } from '../../../../../Core/Service/workflowJobDocument-service';
import AppContext from '../../../../../App/AppContext';
import { BiggerDisabledText } from '../../../../../Core/FormInput/AppTextBox';
import { useTheme } from '@material-ui/core/styles';
import { withStyles } from '@material-ui/core/styles';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import './customer-details-modal.scss'; // Import the SCSS file

const StyledSubTableCell = withStyles(() => ({
    head: { 
        composes: 'table-header' // Using SCSS class
    },
    body: { 
        composes: 'table-body' // Using SCSS class
    }
}))(TableCell);

const StyledTableRow = withStyles(() => ({}))(TableRow);

const CustomerDetailsModal = ({ accountNo, onClose }) => {
    const theme = useTheme();
    const { showToast } = useContext(AppContext);
    const [customerData, setCustomerData] = useState(null);
    const [customerNotes, setCustomerNotes] = useState([]);
    const [loading, setLoading] = useState(true);

    // CSS variables based on theme
    const cssVariables = {
        '--text-secondary': theme.palette.text.secondary,
        '--text-disabled': theme.palette.text.disabled,
        '--text-primary': theme.palette.text.primary,
        '--secondary-main': theme.palette.secondary.main,
        '--secondary-light': theme.palette.secondary.light,
        '--background-paper': theme.palette.background.paper,
        '--divider': theme.palette.divider
    };

    useEffect(() => {
        const fetchCustomerDetails = async () => {
            try {
                setLoading(true);
                const res = await getCustomerDetails(accountNo);

                if (res.success) {
                    if (res.data?.customer?.length > 0) {
                        setCustomerData(res.data.customer[0]);
                    }
                    if (res.data?.customerNotes) {
                        setCustomerNotes(res.data.customerNotes);
                    }
                } else {
                    setCustomerData(null);
                    setCustomerNotes([]);
                }
            } catch (error) {
                setCustomerData(null);
                setCustomerNotes([]);
                showToast('error', 'Error fetching customer data');
            } finally {
                setLoading(false);
            }
        };

        if (accountNo) fetchCustomerDetails();
    }, [accountNo]);

    const renderTitle = (
        <Grid container className="title-container">
            <Grid item xs={12}>
                <Typography variant="h4" className="title" style={{ color: theme.palette.secondary.main }}>
                    Customer Details
                </Typography>
                <Box className="title-line" style={{
                    background: `linear-gradient(90deg, ${theme.palette.secondary.light}, transparent)`
                }}/>
            </Grid>
        </Grid>
    );

    const getDisplayValue = (value) => {
        if (value === null || value === undefined || value === '') {
            return 'NA';
        }
        return value;
    };

    const renderCompactField = (label, value) => {
        const displayValue = getDisplayValue(value);
        const isEmpty = displayValue === 'No record found';
        
        return (
            <Grid item xs={12} sm={6} md={3} className="compact-field">
                <Typography variant="body1" className="label">
                    {label}
                </Typography>
                <Typography 
                    variant="body1" 
                    className={`value ${isEmpty ? 'empty' : ''}`}
                    style={{ 
                        color: isEmpty ? theme.palette.text.disabled : theme.palette.text.primary 
                    }}
                >
                    {displayValue}
                </Typography>
            </Grid>
        );
    };

    const renderTextAreaField = (label, value) => {
        const displayValue = getDisplayValue(value);
        const isEmpty = displayValue === 'NA';

        return (
            <Grid item xs={12} md={6} className="text-area-field">
                <Typography variant="body1" className="label">
                    {label}
                </Typography>
                {isEmpty ? (
                    <Typography variant="body1" className="value empty">
                        {displayValue}
                    </Typography>
                ) : (
                    <BiggerDisabledText
                        value={displayValue}
                        multiline={true}
                        fullWidth
                        className="value"
                        style={{
                            color: theme.palette.text.primary
                        }}
                    />
                )}
            </Grid>
        );
    };

    return (
        <DialogComp 
            title={renderTitle} 
            maxWidth="xl" 
            fullScreen 
            fullWidth 
            onClose={onClose} 
            overflow="auto"
            style={cssVariables}
        >
            <div className="customer-details-modal">
                {loading ? (
                    <Typography className="loading-message">
                        Loading customer data...
                    </Typography>
                ) : customerData ? (
                    <Grid container spacing={2} style={{ padding: '0 16px 16px' }}>
                        <Grid container item xs={12} spacing={2} style={{ marginTop: -15 }}>
                            {renderCompactField('Start-up Order Email', customerData.tCardCustomerStartupOrderEmail)}
                            {renderCompactField('Start-up Order Phone', customerData.tCardCustomerStartupOrderPhone)}
                            {renderCompactField('Spend Limit (£)', customerData.tCardCustomerSpendLimit)}
                            {renderCompactField('Order Number Required', customerData.orderNumberRequired ? 'Yes' : 'No')}
                            {renderCompactField('Authority Contact Email', customerData.tCardCustomerAuthorityContactEmail)}
                            {renderCompactField('Authority Contact Phone', customerData.tCardCustomerAuthorityContactPhone)}
                            {renderCompactField('Tyre Company', customerData.tCardCustomerTyreCompany)}
                            {renderCompactField('Tyre Contact Phone', customerData.tCardCustomerTyreCompanyContactPhone)}
                            {renderCompactField('Final Order Contact Email', customerData.tCardCustomerFinalOrderContactEmail)}
                            {renderCompactField('Final Order Contact Phone', customerData.tCardCustomerFinalOrderContactPhone)}
                            {renderCompactField('Courtesy Car', customerData.tCardCustomerCourtesyCarForInspection ? 'Yes' : 'No')}
                            {renderCompactField('Brake Test Policy', customerData.tCardCustomerBrakeTestPolicy)}
                            {renderCompactField('Preferred Parts Supply', customerData.tCardCustomerPreferredPartsSupply)}
                            {renderCompactField('Overnight VOR Update', customerData.tCardCustomerOvernightVORUpdate)}
                            {renderCompactField('Portal Information', customerData.tCardCustomerPortalInfo)}
                            {renderCompactField('Authority Process', customerData.tCardCustomerAuthorityProcess)}
                            {renderCompactField('Retail Defect Process', customerData.tCardCustomerRetailDefectProcess)}
                            {renderCompactField('Out of Tyre Process', customerData.tCardCustomerOutOfTyreProcess)}
                            {renderCompactField('Final Order Number Process', customerData.tCardCustomerFinalOrderNumberProcess)}
                            {renderCompactField('Additional Information', customerData.tCardCustomerAdditionalInfo)}
                        </Grid>

                        <Grid container item xs={12} spacing={2}>
                            <Grid item xs={12} className="customer-notes-section">
                                <Typography variant="h6" className="customer-notes-title">
                                    Customer Notes
                                </Typography>
                                {customerNotes.length > 0 ? (
                                    <TableContainer className="notes-table-container">
                                        <Table size="small" style={{ minWidth: '100%' }} stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <StyledSubTableCell style={{ width: '70%' }}>Note</StyledSubTableCell>
                                                    <StyledSubTableCell style={{ width: '30%' }}>Type</StyledSubTableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {customerNotes.map((note, index) => (
                                                    <StyledTableRow key={index} style={{ height: 'auto' }}>
                                                        <StyledSubTableCell className="table-cell">
                                                            <div dangerouslySetInnerHTML={{ __html: note.note }} />
                                                        </StyledSubTableCell>
                                                        <StyledSubTableCell className="table-cell">
                                                            {note.type}
                                                        </StyledSubTableCell>
                                                    </StyledTableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Typography className="no-notes-message">
                                        No notes available for this customer
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                ) : (
                    <Typography variant="h6" className="no-data-message">
                        No data found for this customer
                    </Typography>
                )}
            </div>
        </DialogComp>
    );
};

export default CustomerDetailsModal;